// ./src/utils/api/ExpenseScheduleService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Car } from "./CarService";
import { ExpenseKind } from "./AppEssentialsService";
import { Expense } from "./ExpenseService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const EXPENSE_SCHEDULE_FIELDS = `
  id
  accountId
  userId
  carId
  kindId
  scheduleType
  scheduleDays
  startAt
  endAt
  nextScheduledAt
  lastAddedAt
  lastCreatedExpenseId
  whereDone
  costWork
  costParts
  tax
  fees
  subtotal
  totalPrice
  paidInCurrency
  shortNote
  comments
  status
  version
  createdBy
  updatedBy
  createdAt
  updatedAt
  user {
    id
    firstName
    lastName
    email
  }
  car {
    id
    label
  }
  userCreated {
    id
    firstName
    lastName
  }
  userUpdated {
    id
    firstName
    lastName
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const EXPENSE_SCHEDULE_LIST_QUERY = gql`
  query ExpenseScheduleList($filter: ExpenseScheduleFilter, $params: PaginationAndSorting) {
    expenseScheduleList(filter: $filter, params: $params) {
      code
      data {
        ${EXPENSE_SCHEDULE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_SCHEDULE_GET_QUERY = gql`
  query ExpenseScheduleGet($id: ID) {
    expenseScheduleGet(id: $id) {
      code
      data {
        ${EXPENSE_SCHEDULE_FIELDS}
        lastCreatedExpense {
          id
          whenDone
          totalPrice
          paidInCurrency
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_SCHEDULE_GET_MANY_QUERY = gql`
  query ExpenseScheduleGetMany($ids: [ID]) {
    expenseScheduleGetMany(ids: $ids) {
      code
      data {
        ${EXPENSE_SCHEDULE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const EXPENSE_SCHEDULE_CREATE_MUTATION = gql`
  mutation ExpenseScheduleCreate($params: ExpenseScheduleInput) {
    expenseScheduleCreate(params: $params) {
      code
      data {
        ${EXPENSE_SCHEDULE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_SCHEDULE_UPDATE_MUTATION = gql`
  mutation ExpenseScheduleUpdate($where: ExpenseScheduleWhereInput, $params: ExpenseScheduleInput) {
    expenseScheduleUpdate(where: $where, params: $params) {
      code
      data {
        ${EXPENSE_SCHEDULE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_SCHEDULE_PAUSE_MUTATION = gql`
  mutation ExpenseSchedulePause($where: ExpenseScheduleWhereInput) {
    expenseSchedulePause(where: $where) {
      code
      data {
        ${EXPENSE_SCHEDULE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_SCHEDULE_RESUME_MUTATION = gql`
  mutation ExpenseScheduleResume($where: ExpenseScheduleWhereInput) {
    expenseScheduleResume(where: $where) {
      code
      data {
        ${EXPENSE_SCHEDULE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_SCHEDULE_RUN_NOW_MUTATION = gql`
  mutation ExpenseScheduleRunNow($where: ExpenseScheduleWhereInput) {
    expenseScheduleRunNow(where: $where) {
      code
      data {
        ${EXPENSE_SCHEDULE_FIELDS}
      }
      createdExpense {
        id
        whenDone
        totalPrice
        paidInCurrency
        expenseKind {
          id
          code
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_SCHEDULE_REMOVE_MUTATION = gql`
  mutation ExpenseScheduleRemove($where: ExpenseScheduleWhereInput) {
    expenseScheduleRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EXPENSE_SCHEDULE_REMOVE_MANY_MUTATION = gql`
  mutation ExpenseScheduleRemoveMany($where: [ExpenseScheduleWhereInput]) {
    expenseScheduleRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

/**
 * Schedule type constants
 */
export const SCHEDULE_TYPE = {
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  ONE_TIME: 'one-time',
} as const;

export type ScheduleTypeValue = (typeof SCHEDULE_TYPE)[keyof typeof SCHEDULE_TYPE];

/**
 * Expense schedule status constants
 */
export const EXPENSE_SCHEDULE_STATUS = {
  PAUSED: 50,
  ACTIVE: 100,
  COMPLETED: 200,
} as const;

export type ExpenseScheduleStatusValue = (typeof EXPENSE_SCHEDULE_STATUS)[keyof typeof EXPENSE_SCHEDULE_STATUS];

/**
 * Days of week for weekly schedules (ISO format: 1=Monday, 7=Sunday)
 */
export const WEEKDAY = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
} as const;

export type WeekdayValue = (typeof WEEKDAY)[keyof typeof WEEKDAY];

export interface ExpenseSchedule {
  id?: string | undefined;
  accountId?: string | undefined;
  userId?: string | undefined;
  carId?: string | undefined;
  /** Reference to expense_kinds table */
  kindId?: number | undefined;

  // Schedule configuration
  /**
   * Schedule frequency type:
   * - weekly: expenses created on specified days of week (1=Mon, 7=Sun)
   * - monthly: expenses created on specified days of month (1-31, 'last')
   * - yearly: expenses created on specified dates (MM-DD format)
   * - one-time: single future expense on specified date (YYYY-MM-DD)
   */
  scheduleType?: ScheduleTypeValue | undefined;
  /**
   * Days/dates when expenses should be created. Format depends on scheduleType:
   * - weekly: comma-separated day numbers, 1=Monday, 7=Sunday (e.g., "1,3,5")
   * - monthly: comma-separated day numbers, 1-31, 'last' for last day (e.g., "1,15,last")
   * - yearly: comma-separated MM-DD dates (e.g., "01-15,06-15")
   * - one-time: single YYYY-MM-DD date (e.g., "2026-06-20")
   */
  scheduleDays?: string | undefined;
  /** When the schedule becomes active */
  startAt?: string | undefined;
  /** When the schedule stops (null = no end date) */
  endAt?: string | undefined;
  /** Pre-computed next occurrence date */
  nextScheduledAt?: string | undefined;
  /** When an expense was last created from this schedule */
  lastAddedAt?: string | undefined;
  /** Reference to the most recently created expense from this schedule */
  lastCreatedExpenseId?: string | undefined;

  // Expense template fields
  /** Place name (insurance company, parking garage, loan provider, etc.) */
  whereDone?: string | undefined;
  /** Labor cost template */
  costWork?: number | undefined;
  /** Parts cost template */
  costParts?: number | undefined;
  /** Tax amount template */
  tax?: number | undefined;
  /** Fees amount template */
  fees?: number | undefined;
  /** Subtotal in payment currency */
  subtotal?: number | undefined;
  /** Total price in payment currency */
  totalPrice?: number | undefined;
  /** Currency for created expenses (null = use user's home currency at time of creation) */
  paidInCurrency?: string | undefined;
  /** Brief note template for generated expenses */
  shortNote?: string | undefined;
  /** Additional comments template */
  comments?: string | undefined;

  // Status and audit fields
  /**
   * Schedule status:
   * - 50: Paused (temporarily disabled)
   * - 100: Active (will create expenses on schedule)
   * - 200: Completed (for one-time schedules after execution)
   */
  status?: ExpenseScheduleStatusValue | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;

  // Resolved references
  /** User who will be attributed as owner of created expenses */
  user?: User | undefined;
  car?: Car | undefined;
  /** Expense kind details */
  expenseKind?: ExpenseKind | undefined;
  /** Most recently created expense from this schedule */
  lastCreatedExpense?: Expense | undefined;
  userCreated?: User | undefined;
  userUpdated?: User | undefined;
}

export interface ExpenseScheduleInput {
  id?: string | null | undefined;
  accountId?: string | null | undefined;
  carId?: string | null | undefined;
  /** Reference to expense_kinds table */
  kindId?: number | null | undefined;

  // Schedule configuration
  /** Schedule frequency: weekly, monthly, yearly, one-time */
  scheduleType?: ScheduleTypeValue | null | undefined;
  /**
   * Days/dates for expense creation. Format depends on scheduleType:
   * - weekly: "1,3,5" (1=Mon, 7=Sun)
   * - monthly: "1,15,last"
   * - yearly: "01-15,06-15"
   * - one-time: "2026-06-20"
   */
  scheduleDays?: string | null | undefined;
  /** When schedule becomes active (defaults to tomorrow) */
  startAt?: string | null | undefined;
  /** When schedule stops (null = no end) */
  endAt?: string | null | undefined;

  // Expense template fields
  /** Place name for generated expenses */
  whereDone?: string | null | undefined;
  /** Labor cost */
  costWork?: number | null | undefined;
  /** Parts cost */
  costParts?: number | null | undefined;
  /** Tax amount */
  tax?: number | null | undefined;
  /** Fees amount */
  fees?: number | null | undefined;
  /** Subtotal (if not provided, calculated as costWork + costParts) */
  subtotal?: number | null | undefined;
  /** Total price (if not provided, calculated as subtotal + tax + fees) */
  totalPrice?: number | null | undefined;
  /** Currency (null = use user's home currency) */
  paidInCurrency?: string | null | undefined;
  /** Brief note for generated expenses */
  shortNote?: string | null | undefined;
  /** Comments for generated expenses */
  comments?: string | null | undefined;

  /** Status: 50=Paused, 100=Active */
  status?: ExpenseScheduleStatusValue | null | undefined;
}

export interface ExpenseScheduleFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  userId?: string[] | null | undefined;
  carId?: string[] | null | undefined;
  kindId?: number[] | null | undefined;
  /** Filter by schedule type: weekly, monthly, yearly, one-time */
  scheduleType?: ScheduleTypeValue[] | null | undefined;
  /** Filter by status: 50=Paused, 100=Active, 200=Completed */
  status?: ExpenseScheduleStatusValue[] | null | undefined;
  /** Filter schedules with next occurrence on or after this date */
  nextScheduledAtFrom?: string | null | undefined;
  /** Filter schedules with next occurrence on or before this date */
  nextScheduledAtTo?: string | null | undefined;
  /** Search in whereDone, shortNote, comments */
  searchKeyword?: string | null | undefined;
}

export interface ExpenseScheduleWhereInput {
  id?: string | undefined;
}

export interface ExpenseScheduleRunNowResult extends OpResult {
  createdExpense?: Expense | null;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Parse schedule days string into an array
 */
export function parseScheduleDays(scheduleDays: string): string[] {
  return scheduleDays.split(',').map((d) => d.trim());
}

/**
 * Format schedule days array into a string
 */
export function formatScheduleDays(days: (string | number)[]): string {
  return days.map((d) => String(d)).join(',');
}

/**
 * Get human-readable label for a weekday number
 */
export function getWeekdayLabel(day: number): string {
  const labels: Record<number, string> = {
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
    7: 'Sunday',
  };
  return labels[day] || String(day);
}

/**
 * Get human-readable label for schedule type
 */
export function getScheduleTypeLabel(scheduleType: ScheduleTypeValue): string {
  const labels: Record<ScheduleTypeValue, string> = {
    [SCHEDULE_TYPE.WEEKLY]: 'Weekly',
    [SCHEDULE_TYPE.MONTHLY]: 'Monthly',
    [SCHEDULE_TYPE.YEARLY]: 'Yearly',
    [SCHEDULE_TYPE.ONE_TIME]: 'One-time',
  };
  return labels[scheduleType] || scheduleType;
}

/**
 * Get human-readable label for schedule status
 */
export function getScheduleStatusLabel(status: ExpenseScheduleStatusValue): string {
  const labels: Record<ExpenseScheduleStatusValue, string> = {
    [EXPENSE_SCHEDULE_STATUS.PAUSED]: 'Paused',
    [EXPENSE_SCHEDULE_STATUS.ACTIVE]: 'Active',
    [EXPENSE_SCHEDULE_STATUS.COMPLETED]: 'Completed',
  };
  return labels[status] || String(status);
}

/**
 * Format schedule days for display based on schedule type
 */
export function formatScheduleDaysForDisplay(
  scheduleType: ScheduleTypeValue,
  scheduleDays: string
): string {
  const days = parseScheduleDays(scheduleDays);

  switch (scheduleType) {
    case SCHEDULE_TYPE.WEEKLY:
      return days.map((d) => getWeekdayLabel(parseInt(d, 10))).join(', ');

    case SCHEDULE_TYPE.MONTHLY:
      return days
        .map((d) => {
          if (d === 'last') return 'Last day';
          const num = parseInt(d, 10);
          if (num === 1 || num === 21 || num === 31) return `${num}st`;
          if (num === 2 || num === 22) return `${num}nd`;
          if (num === 3 || num === 23) return `${num}rd`;
          return `${num}th`;
        })
        .join(', ');

    case SCHEDULE_TYPE.YEARLY:
      return days
        .map((d) => {
          const [month, day] = d.split('-');
          const date = new Date(2000, parseInt(month, 10) - 1, parseInt(day, 10));
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        })
        .join(', ');

    case SCHEDULE_TYPE.ONE_TIME:
      const date = new Date(scheduleDays);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });

    default:
      return scheduleDays;
  }
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * Expense schedule service that handles all scheduled expense-related API calls.
 * Supports CRUD operations plus pause, resume, and runNow actions.
 */
export class ExpenseScheduleService {
  /**
   * Get a list of expense schedules with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: ExpenseScheduleFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(EXPENSE_SCHEDULE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single expense schedule by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(EXPENSE_SCHEDULE_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple expense schedules by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(EXPENSE_SCHEDULE_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Create a new expense schedule
   */
  static async create(params: ExpenseScheduleInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(EXPENSE_SCHEDULE_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing expense schedule
   */
  static async update(
    where: ExpenseScheduleWhereInput,
    params: ExpenseScheduleInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(EXPENSE_SCHEDULE_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Pause an active schedule (sets status to 50)
   */
  static async pause(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(EXPENSE_SCHEDULE_PAUSE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Resume a paused schedule (sets status to 100)
   */
  static async resume(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(EXPENSE_SCHEDULE_RESUME_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Manually trigger expense creation from a schedule.
   * Creates an expense for today's date regardless of the schedule timing.
   * Useful for testing or catch-up scenarios.
   */
  static async runNow(id: string): Promise<ExpenseScheduleRunNowResult> {
    const result = await ApiService.executeMutation(EXPENSE_SCHEDULE_RUN_NOW_MUTATION, {
      where: { id },
    });
    return result as ExpenseScheduleRunNowResult;
  }

  /**
   * Remove/delete an expense schedule (soft delete)
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(EXPENSE_SCHEDULE_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple expense schedules (soft delete)
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(EXPENSE_SCHEDULE_REMOVE_MANY_MUTATION, {
      where,
    });
    return result;
  }
}